// ── OrgRaOverviewSection — RR-BI-1 Slice 2b: the operational BI spine for /org-risk-acceptances ──
//
// The ORG mirror of the vendor RA operations workspace (RiskAcceptancesIndexPage BI block), ORG-ADAPTED:
//   • attention rows carry NO vendor (org = current tenant);
//   • the expiry runway has a 6th REAL `no_expiry` state (expiry is NULLABLE since O1/V995) — rendered OFF
//     the time axis as a dashed footnote count, never a fabricated time bucket;
//   • the "Expiring in 30 days" KPI states an honest "of N dated" denominator (null-expiry excluded).
// Honesty floor (load-bearing): no blended risk score; residual-NULL is completeness (never low, stays in the
// denominator); inherent + residual are two INDEPENDENT series; empty buckets are real zeros; the funnel's
// in_queue is stated separately, never folded into a rate. Operational BI — NOT a posture re-skin of the
// Enterprise Risk Dashboard (cycle-time / aging / funnel / runway).

import { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import type { TooltipProps } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { InfoTooltip } from '@/components/shared/InfoTooltip';
import { KpiCard } from '@/components/shared/KpiCard';
import { cn } from '@/lib/utils';
import type { OraOverview } from '@/api/types/org-risk-acceptance.types';

// ── Lifecycle strip — the 7 native workflow_status values grouped by who holds it. The `closed` holder
// additionally shows its closure_reason breakdown (display-only sub-counts). Mirrors the vendor strip. ──
const HOLDERS: { label: string; states: { s: string; label: string }[] }[] = [
  { label: 'With submitter', states: [{ s: 'draft', label: 'Draft' }, { s: 'returned', label: 'Returned' }] },
  { label: 'Awaiting approval', states: [{ s: 'pending_approval', label: 'Pending approval' }] },
  { label: 'Active', states: [{ s: 'approved', label: 'Approved' }] },
  { label: 'Closed', states: [{ s: 'denied', label: 'Denied' }, { s: 'withdrawn', label: 'Withdrawn' }, { s: 'closed', label: 'Closed' }] },
];
const CLOSURE_LABEL: { r: string; label: string }[] = [
  { r: 'remediated', label: 'Remediated' }, { r: 'expired', label: 'Expired' }, { r: 'revoked', label: 'Revoked' },
  { r: 'replaced_by_renewal', label: 'Replaced by renewal' }, { r: 'app_service_retired', label: 'App/service retired' },
  { r: 'no_longer_applicable', label: 'No longer applicable' }, { r: 'other', label: 'Other' },
];

// Token hexes (recharts needs color strings; mirror tailwind.config.ts + the vendor chart palette).
const C = { valid: '#3FB980', warn: '#D7A94A', high: '#E07B4F', expired: '#C2453F', cyan: '#009FFD', slate: '#8298B0', axis: '#5A6A82', grid: '#1E2E42' };

function ChartPanel({ title, info, subtitle, children }: { title: string; info: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">{title}<InfoTooltip text={info} /></span>
        {subtitle && <span className="text-[11px] text-text-secondary">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}
function ChartEmpty({ note }: { note: string }) {
  return (
    <div className="h-[170px] flex flex-col items-center justify-center gap-1.5 text-center px-4 border border-dashed border-border-subtle rounded-lg">
      <span className="text-[11px] uppercase tracking-wide text-text-faint">Limited data</span>
      <span className="text-[11.5px] text-text-faint leading-relaxed max-w-[38ch]">{note}</span>
    </div>
  );
}

// Chart 1 — Time-to-expiry runway (approved). ⚠ ORG: the `no_expiry` count is a REAL non-Accept state and is
// rendered OFF the time axis as a dashed footnote (never a fabricated time bucket). 0-count time buckets draw
// no bar (honest gap). The runway is empty only when NO approved rows are dated AND none are undated.
function ExpiryRunway({ r, activeTotal }: { r: OraOverview['expiry_runway']; activeTotal: number }) {
  const dated = r.past + r.d0_30 + r.d31_90 + r.d91_180 + r.d180_plus;
  if (activeTotal === 0) return <ChartEmpty note="No active responses yet — the runway fills in as responses are approved." />;
  const rows = [
    { x: 'Lapsed', n: r.past, fill: C.expired },
    { x: '≤30d', n: r.d0_30, fill: C.high },
    { x: '31–90d', n: r.d31_90, fill: C.warn },
    { x: '91–180d', n: r.d91_180, fill: C.cyan },
    { x: '>180d', n: r.d180_plus, fill: C.valid },
  ];
  return (
    <div className="flex flex-col gap-2">
      {dated === 0 ? (
        <ChartEmpty note="No active response carries an expiry date yet — dated responses appear here; undated ones are counted below." />
      ) : (
        <ResponsiveContainer width="100%" height={170}>
          <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid stroke={C.grid} vertical={false} />
            <XAxis dataKey="x" tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.03)' }} contentStyle={{ background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="n" name="responses" radius={[2, 2, 0, 0]} maxBarSize={40}>
              {rows.map((row) => <Cell key={row.x} fill={row.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      {/* ⚠ ORG No-expiry: a REAL non-Accept state, off the time axis. Rendered as a dashed footnote, NOT a bar
          (dating it would be a fabricated possibility). Shown only when the count is real (>0). */}
      {r.no_expiry > 0 && (
        <div className="flex items-center gap-2 border border-dashed border-border-subtle rounded-md px-2.5 py-1.5 text-[11.5px] text-text-secondary">
          <span className="font-semibold text-text-primary tabular-nums">{r.no_expiry}</span>
          <span>with no expiry — non-Accept responses, not on the time axis.</span>
        </div>
      )}
    </div>
  );
}

// ⚠ Tooltip omits zero series so a severity with no residual never reads a spurious "0".
function SevTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const nz = payload.filter((p) => Number(p.value ?? 0) > 0);
  if (!nz.length) return null;
  return (
    <div style={{ background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12, padding: '6px 10px' }}>
      <div style={{ color: '#EAF2FB', marginBottom: 2 }}>{label}</div>
      {nz.map((p) => <div key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</div>)}
    </div>
  );
}
// Chart 2 — Severity inherent vs residual: TWO INDEPENDENT series; residual-NULL is its own Unspecified column
// (⚠ marked "no inherent" — inherent is NOT NULL so it never has an Unspecified). No composite, ever.
function SeverityChart({ s, activeTotal }: { s: OraOverview['severity']; activeTotal: number }) {
  if (activeTotal === 0) return <ChartEmpty note="No active responses yet — inherent and residual ratings appear once responses are approved." />;
  const cats = ['low', 'medium', 'high', 'critical', 'unspecified'] as const;
  const rows = cats.map((c) => ({
    x: c === 'unspecified' ? 'Unspecified' : c[0].toUpperCase() + c.slice(1),
    inherent: c === 'unspecified' ? 0 : (s.inherent as Record<string, number>)[c],
    residual: (s.residual as Record<string, number>)[c] ?? 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={C.grid} vertical={false} />
        <XAxis dataKey="x" tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
        <Tooltip content={<SevTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
        <Bar dataKey="inherent" name="inherent" fill={C.high} radius={[2, 2, 0, 0]} maxBarSize={22} />
        <Bar dataKey="residual" name="residual" fill={C.cyan} radius={[2, 2, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Chart 3 — Approval funnel: three real decision OUTCOMES (approved/returned/denied), count + share of the
// decided base + median latency. ⚠ decided base = approved+denied+returned ONLY; in_queue is stated SEPARATELY
// (never a rate denominator — gap-not-zero honesty). Empty when nothing has been decided AND queue is empty.
const FUNNEL_META = [
  { key: 'approved' as const, label: 'Approved', latency: 'approved_latency_days' as const, fill: C.valid },
  { key: 'returned' as const, label: 'Returned', latency: 'returned_latency_days' as const, fill: C.warn },
  { key: 'denied' as const, label: 'Denied', latency: 'denied_latency_days' as const, fill: C.expired },
];
function ApprovalFunnel({ f }: { f: OraOverview['funnel'] }) {
  const decided = f.approved + f.denied + f.returned;
  if (decided === 0 && f.in_queue === 0) {
    return <ChartEmpty note="No submissions have been decided yet — approval, return and denial rates appear once responses move through review." />;
  }
  const pct = (n: number) => (decided === 0 ? '—' : `${Math.round((n / decided) * 100)}%`);
  const lat = (n: number | null) => (n == null ? '—' : `${n}d`);
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        {FUNNEL_META.map((m) => (
          <div key={m.key} className="rounded-lg border border-border-subtle bg-bg-base/40 p-3 flex flex-col gap-1.5">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: m.fill }} />{m.label}
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-text-primary tabular-nums">{f[m.key]}</span>
              <span className="text-xs text-text-secondary">· {pct(f[m.key])} of decided</span>
            </div>
            <span className="text-[11px] text-text-secondary pt-1 border-t border-border-subtle/60">
              {f[m.latency] == null ? 'no completed timings yet' : `${lat(f[m.latency])} median to decision`}
            </span>
          </div>
        ))}
      </div>
      {/* ⚠ In-queue is stated separately — NOT counted in the rates above (it is not an outcome). */}
      <div className="flex items-center gap-2 text-[11.5px] text-text-secondary border-t border-border-subtle/60 pt-2.5">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-accent-cyan shrink-0" />
          <span className="font-semibold text-text-primary tabular-nums">{f.in_queue}</span> still awaiting a decision
        </span>
        <span className="text-text-faint">— in the queue now, not counted in the rates above.</span>
      </div>
    </div>
  );
}

// ── Attention union — a VIEW of the same register records; rows NAVIGATE (never mutate filters). No vendor. ──
const ATTN_RAIL: Record<number, string> = { 1: 'bg-accent-rose', 2: 'bg-accent-amber', 3: 'bg-accent-cyan', 4: 'bg-text-muted' };
const ATTN_BADGE: Record<number, string> = {
  1: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border',
  2: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border',
  3: 'bg-accent-cyan/10 text-accent-cyan border-accent-cyan/30',
  4: 'bg-bg-surface-2 text-text-muted border-border-subtle',
};
function AttentionRow({ row, onNavigate }: { row: OraOverview['attention'][number]; onNavigate: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onNavigate(row.id)}
      className="flex items-center justify-between gap-3 py-2.5 -mx-4 px-4 text-left w-full hover:bg-bg-surface-2/60 transition-colors">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={cn('w-0.5 h-8 rounded-full shrink-0', ATTN_RAIL[row.severity] ?? 'bg-text-muted')} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{row.title ?? '—'}</p>
          <p className="text-[11.5px] text-text-secondary truncate">{row.reason}</p>
        </div>
      </div>
      <span className={cn('text-[10px] font-bold rounded-md border px-1.5 py-0.5 shrink-0', ATTN_BADGE[row.severity] ?? ATTN_BADGE[4])}>{row.label}</span>
    </button>
  );
}

// ── KPI card — RR-BI-1 KPI-AFFORDANCE: the tile is the shared KpiCard (drill-through affordance keyed on the
// real onClick, behavior-frozen). KpiRender is the kpiList item shape (its onClick IS the handler). ──
type KpiRender = { key: string; label: string; value: string; sub?: string; info: string; onClick?: () => void };

// KPI list builder — copy states the REAL calc; drill-throughs set the honest filter. ⚠ "Expiring" states an
// "of N dated" denominator (null-expiry excluded — a non-Accept response with no expiry is undated, not safe).
function kpiList(o: OraOverview, setFilter: (k: string, v: string | null) => void, toggleStatus: (s: string) => void, requiresAction: boolean): KpiRender[] {
  const k = o.kpis;
  const day = (n: number | null) => (n == null ? '—' : `${n}d`);
  return [
    { key: 'action', label: 'Requires my action', value: String(k.requires_my_action),
      sub: `${k.awaiting_my_approval} awaiting your approval · ${k.returned_to_me} returned to you`,
      info: 'Responses where you personally are the next move — waiting on your approval as an approver, or returned to you as the submitter. Uses the action filter the backend already supports.',
      onClick: () => setFilter('requires_action_by_user', requiresAction ? null : 'true') },
    { key: 'pending', label: 'Awaiting approval', value: String(k.awaiting_approval),
      sub: k.awaiting_approval > 0 ? `oldest ${day(k.awaiting_approval_oldest_age_days)} in queue` : undefined,
      info: 'Submitted and sitting in the approval queue, whoever the approver is. The sub-line is the real age of the oldest one from its submitted timestamp — queue depth, not an enterprise count.',
      onClick: () => toggleStatus('pending_approval') },
    { key: 'latency', label: 'Median approval latency', value: k.median_approval_latency_days == null ? '—' : `${k.median_approval_latency_days}d`,
      info: 'Median days from submitted to approved, across responses that actually reached approval. Computed from recorded timestamps only — a response still in the queue is not counted as fast; “—” until at least one has completed.' },
    { key: 'expiring', label: 'Expiring in 30 days', value: String(k.expiring_30d),
      sub: `of ${k.dated_active_total} dated · ${k.expiring_with_renewal} have a renewal started`,
      info: 'Approved responses whose expiry date falls inside the next 30 days — the same window the “approaching expiry” filter uses. The denominator is the dated active responses only: a non-Accept response with no expiry is undated and is excluded, never read as “not expiring”.',
      onClick: () => setFilter('approaching_expiry', 'true') },
    { key: 'lapsed', label: 'Lapsed without renewal', value: String(k.lapsed_without_renewal),
      sub: `of ${k.expired_total} expired · derived from lineage`,
      info: 'Expired responses with no child response pointing back at them. There is no stored “renewed” event — this is derived from parent → child lineage, so read it as “no successor record exists”, not a logged decision not to renew.',
      onClick: () => { setFilter('workflow_status', 'closed'); setFilter('closure_reason', 'expired'); setFilter('has_renewals', 'false'); } },
    { key: 'residual', label: 'Residual rating recorded', value: `${k.residual_recorded}/${k.residual_active_total}`,
      info: 'How many active responses carry a residual rating. It is only required when compensating controls are recorded, so a blank is legitimate — a completeness reading, not a compliance score, and the missing ones are never treated as low.' },
  ];
}

// ── Section ─────────────────────────────────────────────────────────────────────
interface OrgRaOverviewSectionProps {
  overview: OraOverview | undefined;
  loading: boolean;
  statusSet: Set<string>;
  closureReason: string | null;
  requiresAction: boolean;
  onSetFilter: (key: string, value: string | null) => void;
  onToggleStatus: (s: string) => void;
  onNavigate: (id: string) => void;
}

export function OrgRaOverviewSection({
  overview, loading, statusSet, closureReason, requiresAction, onSetFilter, onToggleStatus, onNavigate,
}: OrgRaOverviewSectionProps) {
  const [attnShowAll, setAttnShowAll] = useState(false);
  const KPIS: KpiRender[] = overview ? kpiList(overview, onSetFilter, onToggleStatus, requiresAction) : [];

  return (
    <div className="flex flex-col gap-4">
      {/* KPI row */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {loading || !overview
          ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)
          : KPIS.map((k) => <KpiCard key={k.key} label={k.label} value={k.value} sub={k.sub} info={k.info} onClick={k.onClick} />)}
      </div>

      {/* Lifecycle strip — native workflow_status by holder (click to filter, multi); Closed shows its
          closure_reason breakdown as display-only sub-counts. */}
      <div className="bg-bg-surface border border-border-subtle rounded-lg p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-sm font-semibold text-text-primary">Lifecycle — workflow status</span>
          <InfoTooltip text="Every workflow status a risk response can be in, grouped by who holds it. Click a status to add it to the filter — statuses stack. The Closed group breaks down by closure reason (why it closed) below." />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
          {HOLDERS.map((h) => (
            <div key={h.label} className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">{h.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {h.states.map((st) => {
                  const count = overview?.workflow_status_counts[st.s] ?? 0;
                  const active = statusSet.has(st.s);
                  return (
                    <button key={st.s} type="button" onClick={() => onToggleStatus(st.s)}
                      className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                        active ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan'
                          : count > 0 ? 'border-border-subtle text-text-primary hover:border-accent-cyan/50'
                            : 'border-border-subtle/50 text-text-faint')}>
                      <span>{st.label}</span>
                      <span className="font-mono tabular-nums font-semibold">{overview ? count : '·'}</span>
                    </button>
                  );
                })}
              </div>
              {h.label === 'Closed' && overview && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {CLOSURE_LABEL.filter((c) => (overview.closure_reason_counts[c.r] ?? 0) > 0).map((c) => (
                    <button key={c.r} type="button"
                      onClick={() => { onSetFilter('workflow_status', 'closed'); onSetFilter('closure_reason', closureReason === c.r ? null : c.r); }}
                      className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                        closureReason === c.r ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan' : 'border-border-subtle/60 text-text-secondary hover:border-accent-cyan/40')}>
                      <span>{c.label}</span>
                      <span className="font-mono tabular-nums">{overview.closure_reason_counts[c.r]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Charts — expiry runway + severity inherent-vs-residual */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3">
        <ChartPanel title="Time to expiry"
          info="Active responses bucketed by days until their stored expiry date, plus those already lapsed. Non-Accept responses with no expiry are counted separately, off the time axis — they are undated, not “safe”. Empty buckets are real zeros, not gaps to fill."
          subtitle="approved · by days to expiry">
          {overview ? <ExpiryRunway r={overview.expiry_runway} activeTotal={overview.kpis.active_total} /> : <ChartEmpty note="Loading…" />}
        </ChartPanel>
        <ChartPanel title="Severity — inherent vs residual"
          info="The two stored ratings side by side, exactly as declared — nothing is blended into a score. Residual is only required when compensating controls are recorded, so responses without one sit in their own Unspecified column (with no inherent bar) rather than being read as low or dropped from the chart."
          subtitle="approved · two independent series">
          {overview ? <SeverityChart s={overview.severity} activeTotal={overview.kpis.active_total} /> : <ChartEmpty note="Loading…" />}
        </ChartPanel>
      </div>

      {/* Approval funnel / latency */}
      <ChartPanel title="Approval funnel"
        info="How submitted responses resolved — approved, returned or denied — with the median days each took, from recorded submission and decision timestamps. Rates are over decided responses only. Responses still in the approval queue are shown separately below the outcomes, never folded into the approved rate; medians count only responses that actually reached that decision."
        subtitle="decided · median days to decision">
        {overview ? <ApprovalFunnel f={overview.funnel} /> : <ChartEmpty note="Loading…" />}
      </ChartPanel>

      {/* Requires your action — a VIEW of the same register records (four real conditions). Rows NAVIGATE. */}
      <div className="bg-bg-surface border border-severity-high/40 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">Requires your action
            <InfoTooltip text="Responses needing action, from four real conditions: coverage lapsed (expired with no successor) · awaiting your approval (submitted by someone else, you are an approver) · expiring within 30 days · returned to you as the submitter. Each row is a real response in the list below; click to open it." />
          </span>
          {overview && <span className={cn('text-[11px] font-mono font-bold rounded-md border px-1.5 py-0.5', overview.attention.length > 0 ? 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border' : 'bg-bg-surface-2 text-text-muted border-border-subtle')}>{overview.attention.length}</span>}
        </div>
        {!overview ? (
          <p className="text-xs text-text-secondary">—</p>
        ) : overview.attention.length === 0 ? (
          <p className="text-sm text-text-secondary">Nothing needs your action right now.</p>
        ) : (
          <>
            {/* ⚠ RR-SCROLL-1 discipline from the START: overscroll-contain + min-h-0 keep wheel-scroll INSIDE
                this nested list (no chain to the shell). */}
            <div className={cn('flex flex-col divide-y divide-border-subtle overflow-y-auto overscroll-contain min-h-0', !attnShowAll && 'max-h-[360px]')}>
              {(attnShowAll ? overview.attention : overview.attention.slice(0, 10)).map((row) => (
                <AttentionRow key={`${row.id}-${row.signal}`} row={row} onNavigate={onNavigate} />
              ))}
            </div>
            {overview.attention.length > 10 && (
              <button type="button" onClick={() => setAttnShowAll((v) => !v)}
                className="self-start text-xs font-medium text-accent-cyan hover:text-accent-cyan/80 transition-colors">
                {attnShowAll ? 'Show fewer' : `View all ${overview.attention.length}`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
